import { z } from "zod";
import { bookProductionProductSchema } from "./book-production-evidence.js";
export const BOOK_SEMANTIC_PRODUCTION_SNAPSHOT_CONTRACT = "BOOK_SEMANTIC_PRODUCTION_SNAPSHOT_V1";
const safeIdSchema = z
    .string()
    .trim()
    .min(1)
    .max(240)
    .regex(/^[A-Za-z0-9][A-Za-z0-9_.:/-]*$/u);
const safeCodeSchema = z
    .string()
    .trim()
    .min(1)
    .max(120)
    .regex(/^[A-Z0-9][A-Z0-9_]*$/u);
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/u);
const timestampSchema = z.string().datetime({ offset: true });
export const bookSemanticSnapshotSourceBindingV1Schema = z
    .object({
    bindingId: safeIdSchema,
    role: safeCodeSchema,
    recordId: safeIdSchema,
    recordLocator: z
        .object({
        partitionKey: z.string().trim().min(1).max(500),
        sortKey: z.string().trim().min(1).max(500),
        revisionAttribute: safeIdSchema,
    })
        .strict(),
    revision: z.union([z.string().trim().min(1).max(160), z.number().int().nonnegative()]),
    checksumSha256: sha256Schema,
    current: z.literal(true),
})
    .strict();
export const bookSemanticSnapshotDecisionV1Schema = z
    .object({
    decisionId: safeIdSchema,
    kind: safeCodeSchema,
    revision: z.number().int().positive(),
    state: z.literal("ACCEPTED"),
    decidedBy: safeIdSchema,
    decidedAt: timestampSchema,
    sourceBindingIds: z.array(safeIdSchema).min(1).max(500),
    artifactBindingIds: z.array(safeIdSchema).max(500),
    current: z.literal(true),
})
    .strict();
export const bookSemanticSnapshotArtifactBindingV1Schema = z
    .object({
    bindingId: safeIdSchema,
    role: safeCodeSchema,
    artifactId: safeIdSchema,
    objectVersionId: z.string().trim().min(1).max(1_024),
    checksumSha256: sha256Schema,
    approvalId: safeIdSchema,
    approvalRecordLocator: z
        .object({
        partitionKey: z.string().trim().min(1).max(500),
        sortKey: z.string().trim().min(1).max(500),
        revisionAttribute: safeIdSchema,
    })
        .strict(),
    approvalRecordRevision: z.union([
        z.string().trim().min(1).max(160),
        z.number().int().nonnegative(),
    ]),
    approvalRecordChecksumSha256: sha256Schema,
    current: z.literal(true),
})
    .strict();
export const bookSemanticSnapshotAuthorityEvidenceV1Schema = z
    .object({
    evidenceId: safeIdSchema,
    kind: z.enum([
        "RIGHTS",
        "CONSENT",
        "LIKENESS",
        "GUARDIAN_REVIEW",
        "FACT_REVIEW",
        "PRIVACY",
        "SAFETY",
        "ACCESSIBILITY",
        "SOURCE_LOCK",
        "CORRECTION",
        "REVEAL",
        "APPROVAL",
    ]),
    subjectIds: z.array(safeIdSchema).max(100),
    state: z.literal("CURRENT_ACCEPTED"),
    recordId: safeIdSchema,
    recordLocator: z
        .object({
        partitionKey: z.string().trim().min(1).max(500),
        sortKey: z.string().trim().min(1).max(500),
        revisionAttribute: safeIdSchema,
    })
        .strict(),
    recordRevision: z.union([z.string().trim().min(1).max(160), z.number().int().nonnegative()]),
    checksumSha256: sha256Schema,
})
    .strict();
const approvalReferenceSchema = z
    .object({
    decisionId: safeIdSchema,
    artifactBindingId: safeIdSchema.nullable(),
})
    .strict();
const bedtimePolicySchema = z
    .object({
    family: z.literal("BEDTIME"),
    storyPromiseApproval: approvalReferenceSchema,
    visualPromiseApproval: approvalReferenceSchema,
    guardianReviewEvidenceId: safeIdSchema,
    factReviewEvidenceId: safeIdSchema,
    likenessReviewEvidenceId: safeIdSchema,
    manuscriptApproval: approvalReferenceSchema,
    storyboardApproval: approvalReferenceSchema,
    visualDirectionApproval: approvalReferenceSchema,
    sequenceApproval: approvalReferenceSchema,
    accessibilityDescriptionApproval: approvalReferenceSchema,
    finalProofApproval: approvalReferenceSchema,
})
    .strict();
const comicPolicySchema = z
    .object({
    family: z.literal("COMIC"),
    sourceLockEvidenceId: safeIdSchema,
    scriptApproval: approvalReferenceSchema,
    panelPlanApproval: approvalReferenceSchema,
    letteringApproval: approvalReferenceSchema,
    readingOrderApproval: approvalReferenceSchema,
    continuityApproval: approvalReferenceSchema,
    visualApproval: approvalReferenceSchema,
})
    .strict();
const allergenSchema = z.enum([
    "MILK",
    "EGG",
    "FISH",
    "CRUSTACEAN_SHELLFISH",
    "TREE_NUT",
    "PEANUT",
    "WHEAT",
    "SOYBEAN",
    "SESAME",
]);
const recipePolicySchema = z
    .object({
    family: z.literal("RECIPE"),
    sourceLockEvidenceId: safeIdSchema,
    normalizationApproval: approvalReferenceSchema,
    quantityApproval: approvalReferenceSchema,
    ingredientStepRelationshipApproval: approvalReferenceSchema,
    safetyApproval: approvalReferenceSchema,
    scalingStorageDonenessApproval: approvalReferenceSchema,
    allergens: z
        .array(z
        .object({
        allergen: allergenSchema,
        state: z.enum(["PRESENT", "ABSENT", "UNKNOWN"]),
        evidenceId: safeIdSchema,
        disclosureDecisionId: safeIdSchema,
    })
        .strict())
        .length(9),
})
    .strict()
    .superRefine((policy, context) => {
    if (new Set(policy.allergens.map(({ allergen }) => allergen)).size !== 9)
        context.addIssue({
            code: "custom",
            path: ["allergens"],
            message: "Every major allergen must be represented exactly once, including UNKNOWN.",
        });
});
const myStoryPolicySchema = z
    .object({
    family: z.literal("MY_STORY"),
    principalSubjectId: safeIdSchema,
    voiceFidelityApproval: approvalReferenceSchema,
    evidenceApproval: approvalReferenceSchema,
    privacyApproval: approvalReferenceSchema,
    correctionApproval: approvalReferenceSchema,
    uncertaintyApproval: approvalReferenceSchema,
})
    .strict();
const ourStoryPolicySchema = z
    .object({
    family: z.literal("OUR_STORY"),
    principalSubjectIds: z.tuple([safeIdSchema, safeIdSchema]),
    attributionApproval: approvalReferenceSchema,
    revealPolicyApproval: approvalReferenceSchema,
    consentEvidenceIds: z.tuple([safeIdSchema, safeIdSchema]),
    disagreementTreatmentApproval: approvalReferenceSchema,
    individualApprovalDecisionIds: z.tuple([safeIdSchema, safeIdSchema]),
    jointApprovalDecisionId: safeIdSchema,
    unrevealedMaterialCount: z.literal(0),
})
    .strict()
    .superRefine((policy, context) => {
    if (new Set(policy.principalSubjectIds).size !== 2)
        context.addIssue({
            code: "custom",
            path: ["principalSubjectIds"],
            message: "Our Story requires two distinct principals.",
        });
    if (new Set(policy.individualApprovalDecisionIds).size !== 2)
        context.addIssue({
            code: "custom",
            path: ["individualApprovalDecisionIds"],
            message: "Our Story requires a distinct approval from each principal.",
        });
});
export const bookSemanticSnapshotFamilyPolicyV1Schema = z.discriminatedUnion("family", [
    bedtimePolicySchema,
    comicPolicySchema,
    recipePolicySchema,
    myStoryPolicySchema,
    ourStoryPolicySchema,
]);
export const bookSemanticProductionSnapshotV1Schema = z
    .object({
    contract: z.literal(BOOK_SEMANTIC_PRODUCTION_SNAPSHOT_CONTRACT),
    snapshotId: safeIdSchema,
    snapshotDigestSha256: sha256Schema,
    bookId: safeIdSchema,
    revisionId: safeIdSchema,
    sourceRevision: z.number().int().nonnegative(),
    product: bookProductionProductSchema,
    templateId: safeIdSchema,
    sourceBindings: z.array(bookSemanticSnapshotSourceBindingV1Schema).min(1).max(20),
    acceptedDecisions: z.array(bookSemanticSnapshotDecisionV1Schema).min(1).max(2_000),
    // A twelve-turn edition also binds native assets, per-use authorities and
    // exact visual reviews. The combined atomic-authority limit below remains
    // authoritative; do not drop evidence to fit the older 45-item array cap.
    approvedArtifacts: z.array(bookSemanticSnapshotArtifactBindingV1Schema).min(1).max(80),
    authorityEvidence: z.array(bookSemanticSnapshotAuthorityEvidenceV1Schema).min(1).max(25),
    physicalEdition: z
        .object({
        editionPolicyId: safeIdSchema,
        profileId: safeIdSchema,
        profileVersion: safeIdSchema,
        productUid: safeIdSchema,
        interiorPageCount: z.number().int().positive().max(1_000),
        assemblyOrder: z
            .array(z.enum(["COVER", "FRONT_ENDPAPER", "INTERIOR", "BACK_ENDPAPER"]))
            .min(2)
            .max(4),
        geometryTolerancePoints: z.number().positive().max(10),
    })
        .strict(),
    familyPolicy: bookSemanticSnapshotFamilyPolicyV1Schema,
    accessibilityApprovalDecisionId: safeIdSchema,
    digitalEditionApprovalDecisionId: safeIdSchema,
    createdAt: timestampSchema,
    truthBoundary: z.literal("IMMUTABLE_ACCEPTED_PRODUCTION_INPUT_NOT_RENDERED_PRINT_APPROVED_OR_PROVIDER_READY"),
})
    .strict()
    .superRefine((snapshot, context) => {
    if (snapshot.familyPolicy.family !== snapshot.product)
        context.addIssue({
            code: "custom",
            path: ["familyPolicy", "family"],
            message: "Family policy must match the snapshot product.",
        });
    for (const [path, values] of [
        ["sourceBindings", snapshot.sourceBindings.map(({ bindingId }) => bindingId)],
        ["acceptedDecisions", snapshot.acceptedDecisions.map(({ decisionId }) => decisionId)],
        ["approvedArtifacts", snapshot.approvedArtifacts.map(({ bindingId }) => bindingId)],
        ["authorityEvidence", snapshot.authorityEvidence.map(({ evidenceId }) => evidenceId)],
    ])
        if (new Set(values).size !== values.length)
            context.addIssue({
                code: "custom",
                path: [path],
                message: `${path} identifiers must be unique.`,
            });
    if (new Set(snapshot.acceptedDecisions.map(({ kind }) => kind)).size !==
        snapshot.acceptedDecisions.length)
        context.addIssue({
            code: "custom",
            path: ["acceptedDecisions"],
            message: "A production snapshot may contain only one current decision for each kind.",
        });
    const authorityRecordKeys = new Set([
        ...snapshot.sourceBindings.map(({ recordLocator }) => `${recordLocator.partitionKey}\u0000${recordLocator.sortKey}`),
        ...snapshot.authorityEvidence.map(({ recordLocator }) => `${recordLocator.partitionKey}\u0000${recordLocator.sortKey}`),
        ...snapshot.approvedArtifacts.map(({ approvalRecordLocator }) => `${approvalRecordLocator.partitionKey}\u0000${approvalRecordLocator.sortKey}`),
    ]);
    if (authorityRecordKeys.size + snapshot.acceptedDecisions.length > 94)
        context.addIssue({
            code: "custom",
            path: ["acceptedDecisions"],
            message: "The exact authority and current-decision checks exceed one atomic print-render transaction.",
        });
    const sourceIds = new Set(snapshot.sourceBindings.map(({ bindingId }) => bindingId));
    const artifactIds = new Set(snapshot.approvedArtifacts.map(({ bindingId }) => bindingId));
    snapshot.acceptedDecisions.forEach((decision, index) => {
        if (decision.sourceBindingIds.some((id) => !sourceIds.has(id)))
            context.addIssue({
                code: "custom",
                path: ["acceptedDecisions", index, "sourceBindingIds"],
                message: "Accepted decisions may reference only frozen current sources.",
            });
        if (decision.artifactBindingIds.some((id) => !artifactIds.has(id)))
            context.addIssue({
                code: "custom",
                path: ["acceptedDecisions", index, "artifactBindingIds"],
                message: "Accepted decisions may reference only frozen current artifacts.",
            });
    });
});
//# sourceMappingURL=book-production-snapshot.js.map